import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Check, RotateCcw } from 'lucide-react';
import { Button, TaskProgressWidget } from '@ui';
import { sendCataloguePdfWhatsAppApi } from '../services/api';
import { generateCataloguePdfBlob } from '../utils/cataloguePdf';

const CataloguePdfSendContext = createContext(null);

const BUSY_STATUSES = ['building', 'uploading'];

const JOB_ITEM_STATUS = {
  building: 'active',
  uploading: 'active',
  sent: 'success',
  failed: 'error',
};

function describeJob(job) {
  const total = job.result?.total ?? job.recipientCount;
  switch (job.status) {
    case 'building':
      return 'Menyusun PDF katalog…';
    case 'uploading':
      return 'Mengunggah PDF ke server…';
    case 'sent':
      return job.result?.run_id
        ? `Dikirim bertahap ke ${total} penerima dalam ${job.result.batch_count} batch`
        : `Sedang dikirim ke ${total} penerima`;
    default:
      return `${total} penerima${job.batching ? ` · ${job.batching.batches.length} batch` : ''}`;
  }
}

/**
 * Background sender for catalogue PDFs. The send modal hands a job over and closes; building the
 * PDF (in the browser) and uploading it run here, outside any page, so closing the modal or
 * moving to another page does not interrupt them. Once the server accepts a job the broadcast
 * runs on the server (gateway pacing, batch cooldowns) and is followed and controlled in the
 * Broadcast Monitor. Only closing or reloading the tab before the upload finishes stops a job,
 * and the browser asks first.
 */
export function CataloguePdfSendProvider({ children }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);

  const busy = jobs.some((job) => BUSY_STATUSES.includes(job.status));
  const failedCount = jobs.filter((job) => job.status === 'failed').length;
  const sentJobs = jobs.filter((job) => job.status === 'sent');

  const updateJob = (id, patch) => setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, ...patch } : job)));

  const runJob = useCallback(async (job) => {
    updateJob(job.id, { status: 'building', error: null });
    try {
      const { blob, filename } = await generateCataloguePdfBlob({ items: job.items, catalogue: job.catalogue });
      updateJob(job.id, { status: 'uploading' });
      const result = await sendCataloguePdfWhatsAppApi(
        job.catalogue.catalogue_code,
        blob,
        filename,
        job.recipients,
        job.messages,
        job.throttle,
        job.batching,
      );
      updateJob(job.id, { status: 'sent', result });
    } catch (err) {
      updateJob(job.id, { status: 'failed', error: err?.message || 'Katalog PDF gagal dikirim.' });
    }
  }, []);

  /**
   * Queue a catalogue PDF send and start it right away.
   * @param {{ catalogue: object, items: object[], recipients: object[], messages: object,
   *   throttle: { min: number, max: number }, batching?: object|null }} payload
   *   same arguments as sendCataloguePdfWhatsAppApi, plus the catalogue items the PDF is built from
   */
  const startSend = useCallback(
    (payload) => {
      const job = {
        id: `pdf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        status: 'building',
        batching: null,
        ...payload,
        recipientCount: payload.batching
          ? payload.batching.batches.reduce((sum, batch) => sum + batch.recipients.length, 0)
          : payload.recipients.length,
      };
      setJobs((prev) => [...prev, job]);
      setIsMinimized(false);
      runJob(job);
      return job.id;
    },
    [runJob],
  );

  const retryFailed = () => jobs.filter((job) => job.status === 'failed').forEach(runJob);

  const dismissFinished = () => setJobs((prev) => prev.filter((job) => BUSY_STATUSES.includes(job.status)));

  const openMonitor = () => {
    const latest = sentJobs[sentJobs.length - 1];
    const campaignId = latest?.result?.campaign_id;
    navigate(campaignId ? `/broadcast/monitor?campaign_id=${campaignId}` : '/broadcast/monitor');
    setIsMinimized(true);
  };

  // The PDF lives only in this tab until it is uploaded: ask before the tab is closed or reloaded
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const status = busy ? 'running' : failedCount > 0 ? 'error' : 'completed';
  const title = busy
    ? 'Menyiapkan Katalog PDF'
    : failedCount > 0
      ? 'Pengiriman Katalog Gagal'
      : 'Katalog Diteruskan ke WhatsApp';

  const pendingBatched = jobs.some((job) => job.batching && BUSY_STATUSES.includes(job.status));

  return (
    <CataloguePdfSendContext.Provider value={{ startSend, jobs, busy, pendingBatched }}>
      {children}

      <TaskProgressWidget
        isOpen={jobs.length > 0}
        title={title}
        isMinimized={isMinimized}
        onToggleMinimize={() => setIsMinimized((prev) => !prev)}
        onClose={busy ? () => setIsMinimized(true) : dismissFinished}
        status={status}
        progress={{ current: jobs.length - jobs.filter((job) => BUSY_STATUSES.includes(job.status)).length, total: jobs.length }}
        stats={[
          { label: 'Diteruskan', value: sentJobs.length, variant: 'success' },
          { label: 'Gagal', value: failedCount, variant: failedCount > 0 ? 'danger' : 'neutral' },
        ]}
        items={jobs.map((job) => ({
          id: job.id,
          title: job.catalogue.catalogue_name || job.catalogue.catalogue_code,
          subtitle: describeJob(job),
          status: JOB_ITEM_STATUS[job.status] ?? 'pending',
          error: job.status === 'failed' ? job.error : null,
          badge: job.batching ? 'Bertahap' : 'PDF',
          badgeVariant: job.batching ? 'accent' : 'neutral',
        }))}
        actions={
          <>
            {sentJobs.length > 0 && (
              <Button size="xs" variant="primary" icon={Activity} onClick={openMonitor}>
                Buka Monitor Broadcast
              </Button>
            )}
            {failedCount > 0 && !busy && (
              <Button size="xs" variant="secondary" icon={RotateCcw} onClick={retryFailed}>
                Kirim Ulang
              </Button>
            )}
            {!busy && (
              <Button size="xs" variant="secondary" icon={Check} onClick={dismissFinished}>
                Selesai & Tutup
              </Button>
            )}
          </>
        }
        footerNotice={
          busy
            ? 'Jendela dan halaman lain boleh ditutup, tetapi jangan tutup tab ini sampai PDF selesai diunggah.'
            : 'Progres dan kontrol pengiriman (hentikan, kirim ulang batch) ada di Monitor Broadcast.'
        }
      />
    </CataloguePdfSendContext.Provider>
  );
}

/**
 * Hand catalogue PDF sends to the background sender. `pendingBatched` is true while a batched
 * send is still being uploaded (the server allows one batched send at a time).
 */
// eslint-disable-next-line react-refresh/only-export-components -- provider and hook belong together
export function useCataloguePdfSend() {
  return useContext(CataloguePdfSendContext);
}
